import { ref } from 'vue'
import { LightModeType, lightModuleList, LightEffect, lightAndName, LightColorType, LightDirectionType, DirectionList, LightEffectMode, LightEffectModeType } from '@/define/light'
import { getCurrentDevice } from '@/profiles/selector'
import { setData } from './data'
import { DriverService } from './driver'
import { DriverSocketMsgType } from '@libs/types/message'
import { hexToInteger } from '@/tool/utility'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { DeviceCapability } from '../device_capability'
import { DecorateLight } from '../response'
import { delay } from '@libs/utils/tools'

export const lightDefaultColor: string[] = ['#ff0000', '#F5A100', '#FFEA00', '#AEFF00', '#2EFF9D', '#38FFF2', '#39BAFF', '#0021FF', '#B11FFF']

// 获取当前设备
const currentDevice = getCurrentDevice
// 页面大小
const pageSize = 10

class LightSet {
  private lightModeName = ref('')
  foregroundColor = ref(0)
  backgroundColor = ref(0)
  direction = ref(0)
  isFullColor = ref(true)
  isFGSelected = ref(true)
  //  isLightOn=ref(true)

  private deactivatedModeName = ref('')
  selectedDecorateIndex = ref(0) // 选择的装饰灯效索引
  // lightData = ref<DecorateLight>({} as DecorateLight)

  currentModeTypeTab = ref(LightModeType.None)

  // 当前页码
  currentPage = ref(1)
  // 计算总页数
  totalPages = () => Math.ceil(this.getModeEffects(this.currentModeTypeTab.value).length / pageSize)

  // 切换页面
  goToPage = (page: number) => {
    this.currentPage.value = page
  }
  private lightEffectList: { [name: string]: number } = {}
  constructor() {
    this.revertLightingOnMusicSocketClose()
  }
  // TODO:外部如需修改请用set，使用JSON.parse(JSON.stringify())深拷贝，防止外部污染
  get lightData() {
    if (this.currentModeTypeTab.value == LightModeType.Side) {
      return JSON.parse(JSON.stringify(setData.getData().Decorates[this.selectedDecorateIndex.value]))
    } else {
      return {
        LightLastIsCustom: setData.getData().LightLastIsCustom,
        LightMode: JSON.parse(JSON.stringify(setData.getData().LightMode)),
        ColorsCustom: JSON.parse(JSON.stringify(setData.getData().LightCustom)),
        ColorsDynamic: JSON.parse(JSON.stringify(setData.getData().ColorsDynamic)),
      }
    }
  }

  set lightData(value: DecorateLight) {
    if (this.currentModeTypeTab.value == LightModeType.Side) {
      const old = setData.getData().Decorates[this.selectedDecorateIndex.value]
      if (JSON.stringify(old) !== JSON.stringify(value)) {
        setData.getData().Decorates[this.selectedDecorateIndex.value] = value
        setData.saveLightDecoration(JSON.stringify(old.ColorsCustom) === JSON.stringify(value.ColorsCustom))
      }
    } else {
      const oldCustom = setData.getData().LightCustom
      const oldDynamic = setData.getData().ColorsDynamic
      const oldMode = setData.getData().LightMode
      if (JSON.stringify(oldDynamic) !== JSON.stringify(value.ColorsDynamic)) {
        setData.getData().ColorsDynamic.Mode = value.ColorsDynamic.Mode
        setData.getData().ColorsDynamic.Color = value.ColorsDynamic.Color
        setData.getData().ColorsDynamic.FullColor = value.ColorsDynamic.FullColor
        setData.getData().ColorsDynamic.Run = value.ColorsDynamic.Run
      }
      if (setData.getData().LightLastIsCustom !== value.LightLastIsCustom || JSON.stringify(oldCustom) !== JSON.stringify(value.ColorsCustom)) {
        setData.getData().LightLastIsCustom = value.LightLastIsCustom
        setData.getData().LightCustom = value.ColorsCustom
        setData.setKeysColor(value.ColorsCustom, value.ColorsCustom)
      }
      if (JSON.stringify(oldMode) !== JSON.stringify(value.LightMode)) {
        setData.getData().LightMode = value.LightMode
        // TODO:因为自定义的灯效会修改Light值，所以判断自定义改灯效亮度
        if (this.currentModeTypeTab.value == LightModeType.Custom) {
          setData.setKeysColor(value.ColorsCustom, value.ColorsCustom)
        } else {
          setData.saveLightMode()
        }
      }
    }
  }

  get modeName() {
    return this.currentModeTypeTab.value === LightModeType.Side ? this.deactivatedModeName.value : this.lightModeName.value
  }
  set modeName(value: string) {
    if (this.currentModeTypeTab.value === LightModeType.Side) {
      this.deactivatedModeName.value = value
    } else {
      this.lightModeName.value = value
    }
  }

  // 计算分页后的数据
  paginatedData = () => {
    const startIndex = (this.currentPage.value - 1) * pageSize
    const endIndex = startIndex + pageSize
    return Object.values(this.getModeEffects(this.currentModeTypeTab.value)).slice(startIndex, endIndex)
  }

  tagVisibility(modeType: LightModeType): boolean {
    return this.getModeEffects(modeType).length > 0
  }

  changeLightModeType(modeType: LightModeType) {
    this.currentModeTypeTab.value = modeType
    this.resetCurrentPage()
  }

  private resetCurrentPage() {
    this.currentPage.value = 1
  }

  // 自动跳转到当前选中灯效所在的页码
  scrollToSelectedEffectPage() {
    const effects = this.getModeEffects(this.currentModeTypeTab.value)
    const selectedIndex = effects.findIndex((item) => item.name === this.modeName)
    if (selectedIndex !== -1) {
      const targetPage = Math.floor(selectedIndex / pageSize) + 1
      this.currentPage.value = targetPage
    }
  }

  changeLightingEffect(value: lightAndName) {
    this.modeName = value.name
    if (lightSet.currentModeTypeTab.value == LightModeType.Side) {
      this.buildDirectionData(value)
    } else if (this.currentModeTypeTab.value == LightModeType.MUSIC) {
      this.buildMusicData(value.mode)
    } else if (this.currentModeTypeTab.value == LightModeType.Custom) {
      this.buildCustomData(value.mode)
      if (this.foregroundColor.value == 0) {
        this.foregroundColor.value = hexToInteger(lightDefaultColor[0])
      }
    } else {
      if (value.name === LightEffectMode.Static_pro && this.foregroundColor.value === 0) {
        this.foregroundColor.value = hexToInteger(lightDefaultColor[0])
      }
      this.buildNormalEffectData(value.mode, value.name)
    }
  }

  buildDirectionData(value: lightAndName) {
    this.lightData = {
      ...this.lightData,
      LightMode: {
        ...this.lightData.LightMode,
        Mode: value.mode,
      },
      LightLastIsCustom: value.name.includes('自定义'),
    }
  }

  // 先发送关闭自定义灯效，再发送音乐律动的灯效，避免冲突
  private async buildMusicData(mode: number) {
    if (this.lightData.LightLastIsCustom) {
      this.lightData = {
        ...this.lightData,
        LightLastIsCustom: false,
      }
      await delay(100)
    }
    this.lightData = {
      ...this.lightData,
      ColorsDynamic: {
        ...this.lightData.ColorsDynamic,
        Run: true,
        Mode: mode,
        Color: this.foregroundColor.value,
        FullColor: this.isFullColor.value,
      },
    }
  }

  // 先发送关闭音乐律动灯效，等待回调后再发送自定义灯效，避免冲突
  private buildCustomData(_mode: number) {
    if (this.lightData.ColorsDynamic.Run) {
      this.lightData = {
        ...this.lightData,
        ColorsDynamic: { ...this.lightData.ColorsDynamic, Run: false },
      }
      setData.subscribeOnDynamicStateChange((isRunning: boolean) => {
        if (!isRunning) {
          this.lightData = {
            ...this.lightData,
            LightLastIsCustom: true,
          }
        }
      })
    } else {
      this.lightData = {
        ...this.lightData,
        LightLastIsCustom: true,
      }
    }
  }

  private async buildNormalEffectData(mode: number, name: string) {
    const lightSetData = this.lightData
    if (this.lightData.ColorsDynamic.Run) {
      lightSetData.ColorsDynamic.Run = false
    } else if (this.lightData.LightLastIsCustom) {
      lightSetData.LightLastIsCustom = false
      this.lightData = {
        ...lightSetData,
      }
      await delay(100)
    }
    lightSetData.LightMode.Mode = mode
    lightSetData.LightMode.Color = this.foregroundColor.value
    if (lightModuleList[name] && lightModuleList[name].isNeon) {
      lightSetData.LightMode.FullColor = this.isFullColor.value
    }
    if (this.effectDirectionList().length > 0) {
      const list = this.currentDirectionList(this.modeName)
      lightSetData.LightMode.Direction = list[this.effectDirectionList()[0]]
    } else {
      lightSetData.LightMode.Direction = 0
    }

    if (this.lightData.ColorsDynamic.Run) {
      this.lightData = {
        ...this.lightData,
        ColorsDynamic: { ...this.lightData.ColorsDynamic, Run: false },
      }
      setData.subscribeOnDynamicStateChange((isRunning: boolean) => {
        if (!isRunning) {
          this.lightData = {
            ...lightSetData,
          }
        }
      })
    } else {
      this.lightData = {
        ...lightSetData,
      }
    }
  }

  async syncInitialLightData() {
    this.lightEffectList = await getCurrentDevice.value.getLightEffectList()
    if (this.lightEffectList) {
      this.resetCurrentPage()
      if (setData.getData().Decorates.length > 0) {
        this.initDecorateLightData()
      }
      if (setData.getData().ColorsDynamic.Run) {
        this.initMusicLightData()
      } else if (setData.getData().LightLastIsCustom) {
        this.initCustomLightData()
      } else {
        this.initNormalLightData()
      }
    }
  }

  private async initMusicLightData() {
    for (let key in this.lightEffectList) {
      if (lightModuleList[key] && setData.getData().ColorsDynamic.Mode == this.lightEffectList[key] && lightModuleList[key].lightEffectType == LightModeType.MUSIC) {
        this.lightModeName.value = key
        this.currentModeTypeTab.value = LightModeType.MUSIC
        this.foregroundColor.value = setData.getData().ColorsDynamic.Color
        this.isFullColor.value = setData.getData().ColorsDynamic.FullColor
      }
    }
  }

  private async initCustomLightData() {
    for (let key in this.lightEffectList) {
      if (lightModuleList[key] && lightModuleList[key].lightEffectType == LightModeType.Custom) {
        this.lightModeName.value = key
        this.currentModeTypeTab.value = LightModeType.Custom
        this.foregroundColor.value = hexToInteger(lightDefaultColor[0])
      }
    }
  }

  private async initNormalLightData() {
    let flag = false
    for (let key in this.lightEffectList) {
      if (lightModuleList[key] && setData.getData().LightMode.Mode == this.lightEffectList[key] && lightModuleList[key].lightEffectType != LightModeType.MUSIC && lightModuleList[key].lightEffectType != LightModeType.Side) {
        flag = true
        this.initBuildNormalData(key)
      }
    }
    if (!flag) {
      this.currentModeTypeTab.value = LightModeType.RGB
    }
  }

  private initDecorateLightData() {
    for (let key in this.lightEffectList) {
      const decorate = setData.getData().Decorates[this.selectedDecorateIndex.value]
      if (decorate.LightMode.Mode == this.lightEffectList[key]) {
        this.deactivatedModeName.value = key
      }
    }
  }

  private initBuildNormalData(name: string) {
    if (!lightModuleList[name]) return
    this.lightModeName.value = name
    this.currentModeTypeTab.value = lightModuleList[name].lightEffectType
    this.foregroundColor.value = setData.getData().LightMode.Color
    this.backgroundColor.value = setData.getData().LightMode.ColorBG
    this.direction.value = setData.getData().LightMode.Direction
    this.isFullColor.value = setData.getData().LightMode.FullColor
  }

  private getModeEffects(modeType: LightModeType): lightAndName[] {
    return Object.entries(this.lightEffectList)
      .filter(([effect, _]) => lightModuleList[effect] && lightModuleList[effect].lightEffectType === modeType)
      .map(([effect, index]) => ({
        mode: index,
        name: effect,
      }))
  }
  getEffectAttach(): LightEffect | undefined {
    return lightModuleList[this.modeName]
  }

  getSpeed() {
    return currentDevice.value.speedAllowValues
  }
  getBrightness() {
    return currentDevice.value.lightAllowValues
  }

  getMaxSpeed(): number {
    if (currentDevice.value.speedAllowValues.length > 0) {
      return currentDevice.value.speedAllowValues[currentDevice.value.speedAllowValues.length - 1]
    } else {
      return 0
    }
  }
  getMinSpeed(): number {
    if (currentDevice.value.speedAllowValues.length > 0) {
      return currentDevice.value.speedAllowValues[0]
    } else {
      return 0
    }
  }
  getMaxBrightness(): number {
    if (currentDevice.value.lightAllowValues.length > 0) {
      return currentDevice.value.lightAllowValues[currentDevice.value.lightAllowValues.length - 1]
    } else {
      return 0
    }
  }
  getMinBrightness(): number {
    if (currentDevice.value.lightAllowValues.length > 0) {
      return currentDevice.value.lightAllowValues[0]
    } else {
      return 0
    }
  }
  effectIsSelected(mode: number): boolean {
    if (this.currentModeTypeTab.value == LightModeType.MUSIC) {
      return this.lightData.ColorsDynamic.Run && this.lightData.ColorsDynamic.Mode === mode
    }
    if (this.currentModeTypeTab.value == LightModeType.Custom) {
      return this.lightData.LightLastIsCustom
    }
    return this.lightData.LightMode.Mode == mode
    // if (setData.getData().ColorsDynamic.Run) {
    //   if (setData.getData().ColorsDynamic.Mode === index && this.currentModeTypeTab.value == LightModeType.MUSIC) {
    //     return setData.getData().ColorsDynamic.Mode == index
    //   } else {
    //     return false
    //   }
    // } else if (setData.getData().LightLastIsCustom) {
    //   return this.currentModeTypeTab.value == LightModeType.Custom
    // } else {
    //   if (this.currentModeTypeTab.value == LightModeType.MUSIC) {
    //     return false
    //   } else {
    //     return this.lightData.LightMode.Mode == index
    //   }
    // }
  }

  directionIsSelected(direction: string): boolean {
    const list = this.currentDirectionList(this.modeName)
    return this.lightData.LightMode.Direction == list[direction]
  }

  changeEffectDirection(direction: string) {
    const list = this.currentDirectionList(this.modeName)
    this.lightData = {
      ...this.lightData,
      LightMode: { ...this.lightData.LightMode, Direction: list[direction] },
    }
  }

  effectColorTypeList(): LightColorType[] {
    return lightModuleList[this.modeName]?.LightColor || []
  }
  effectDirectionList(): LightDirectionType[] {
    return lightModuleList[this.modeName]?.LightDirection || []
  }
  onlyFGColor(): boolean {
    const attach = lightModuleList[this.modeName]
    return attach?.LightColor.length == 1 && attach?.LightColor[0] == LightColorType.foreground
  }
  setBGColorSelected(selected: boolean) {
    this.isFGSelected.value = selected
  }

  isSpecialEffect(): boolean {
    return this.modeName == '常亮_1'
  }

  clear() {
    this.currentModeTypeTab.value = LightModeType.None
  }

  /**
   * 获取晶泰设备特定灯光模式的方向值映射
   *
   * 适配规则：
   * 1. 仅针对晶泰设备（通过 specialCheckCurrent 判断）
   * 2. 仅限 Scanning_1（扫描模式）和 SpinningWindmill_1（旋转风车模式）和 SineWave_1 （正弦波模式）
   * 3. 将默认顺时针值 0 和逆时针值 1 反转
   *
   * @param currentLightMode 当前灯光效果模式
   * @returns {Object} 适配后的方向映射表（特殊设备+特定模式返回反转值，其他情况返回默认映射）
   *
   */
  private currentDirectionList = (currentLightMode: LightEffectModeType) => {
    if (specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V1) && (currentLightMode === LightEffectMode.Scanning_1 || currentLightMode === LightEffectMode.SpinningWindmill_1 || currentLightMode === LightEffectMode.SineWave_1)) {
      return {
        ...DirectionList, // 继承基础映射
        clockwise: 1, // 覆盖 → 顺时针为 1（原值 0）
        counterclockwise: 0, // 覆盖 → 逆时针为 0（原值 1）
      }
    }
    return DirectionList
  }

  // 音乐律动是否可用
  isMusicRhythmAvailable = ref(false)
  // 检测音乐律动灯光可用状态
  checkMusicRhythmAvailableStatus = async () => {
    DeviceCapability.isMusicRhythmAvailable().then((res) => {
      this.isMusicRhythmAvailable.value = res
    })
  }

  // 当音乐律动socket关闭时，切到第一个灯光模式
  revertLightingOnMusicSocketClose = () => {
    DriverService.instance.setHandler(DriverSocketMsgType.MSMusicSocketClose, () => {
      this.isMusicRhythmAvailable.value = false
      if (setData.getData().ColorsDynamic.Run) {
        this.changeLightModeType(LightModeType.RGB)
        const data = this.paginatedData()
        if (data.length > 0) {
          // TODO:这个方法会发多次请求，需要优化
          this.changeLightingEffect(data[1])
        }
      }
    })
  }
}

const lightSet = new LightSet()

export { lightSet }
